import React, { Suspense, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { isDesktop } from 'react-device-detect';
import { registerForIncidentUpdates } from '../../state/modules/incidents/operations';
import { dashboardBgColor } from './ui-kit/utils';
import { isMobileOnly } from 'react-device-detect';
import { operations as dashboardOperations, selectors as dashboardSelectors } from '../../state/modules/dashboards';
import { operations as archetypeOperations } from '../../state/modules/widget-archetypes';
import { selectors as widgetArchetypeSelectors } from '../../state/modules/widget-archetypes';
import { operations as activityTypeOperations } from '../../state/modules/activity-types';
import { getCurrentUser } from '../../state/modules/current-user/operations';
import DashboardRegistrationManager from './DashboardRegistrationManager';
import DashboardAPIManager from './DashboardAPIManager';
import DashboardToolbar from './dashboard-toolbar/DashboardToolbar';
import WidgetStore from './WidgetStore';
import NewDashboardModal from './NewDashboardModal';
import UnsavedChangesWarningModal from './UnsavedChangesWarningModal';
import ReturnToNormalModeModal from './ReturnToNormalModeModal';
import DashboardSettingsDialog from './DashboardSettingsDialog';
import {
  stationToIncidentsMap,
  anyActiveIncidents,
  selectDisplayableIncidents,
  selectCurrentDisplayedIncident,
  displayedIncidentId
} from '../../state/modules/incidents/selectors';
import { redirectTo } from '../../utils/redirect';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ConnectionStatus from '../general/ConnectionStatus';
import DashboardLoadingStatus from './DashboardLoadingStatus';
import Loader from '../general/Loader';
import DashboardPageResponsive from './DashboardPageResponsive';
import { PortalDestination } from '../ui-kit';
import FontSizeMultiplierContext from './FontSizeMultiplierContext';
import AccessRightsContext from '../contexts/AccessRightsContext';

const isSingleWidgetMode = dashboard => dashboard.tag === 'region_map';

const calculateShouldDisplayToolbar = ({
  dashboard,
  dashboards,
  editRights,
  stationMonitor,
  anyActiveIncidents,
  incidentMode
}) => {
  if (isSingleWidgetMode(dashboard)) return false;
  if (incidentMode && anyActiveIncidents) return false;
  if (stationMonitor) return false;

  if (dashboard.always_show_toolbar_for_normal_users) return true;

  // if you have no edit rights, and there is only one dashboard, hide the
  // toolbar. This is to simplify the view for non expert users
  if (!editRights && dashboards.length === 1) return false;
  // if there are two dashboards, and one is the default dashboard, and the
  // other is the station monitor dashboard, hide the toolbar.
  if (!editRights && dashboards.length === 2) {
    const defaultDashboard = dashboards.find(d => d.default);
    const defaultStationMonitorDashboard = dashboards.find(d => d.default_for_station_monitor);

    // If both defaults have values, and they don't have the same ID,
    // hide the toolbar
    if (
      defaultDashboard &&
      defaultStationMonitorDashboard &&
      defaultDashboard.id !== defaultStationMonitorDashboard.id
    ) {
      return false;
    }
  }

  return true;
};

const Dashboard = props => {
  const { dashboard, accessRights } = props;
  const dashboardId = dashboard?.id;

  // edit mode is stored in redux
  const setEditMode = props.setDashboardEditMode;
  const editMode = props.dashboardEditMode;

  const [showWidgetStore, setShowWidgetStore] = useState(false);
  const [showNewDashboardModal, setShowNewDashboardModal] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showReturnToNormalMode, setShowReturnToNormalMode] = useState(false);
  const [showDashboardSettings, setShowDashboardSettings] = useState(false);
  const [showUnsavedChangesWarning, setShowUnsavedChangesWarning] = useState(false);

  const fontSizeMultiplier = isMobileOnly ? 100 : dashboard.font_size_multiplier;

  // State change functions
  const markAsChanged = useCallback(() => setUnsavedChanges(true), []);

  const enterEditMode = useCallback(() => setEditMode(true), []);

  const exitEditMode = useCallback(
    force => {
      if (unsavedChanges && !force) {
        setShowUnsavedChangesWarning(true);
      } else {
        setEditMode(false);
      }
    },
    [unsavedChanges]
  );

  // Whenever the 'New dashboard' modal is opened, load the templates
  useEffect(() => {
    if (showNewDashboardModal) props.fetchAllDashboards({ forceFetch: true, params: { templates: true } });
  }, [showNewDashboardModal]);

  const hideUnsavedChangesWarning = useCallback(() => setShowUnsavedChangesWarning(false), []);

  const returnToNormalMode = useCallback(() => {
    setUnsavedChanges(false);
    setShowWidgetStore(false);
    setShowReturnToNormalMode(false);
    setEditMode(false);

    props.saveDashboardToBackend(dashboardId);
  }, [dashboardId]);

  // Callbacks (used by childen component)
  const createDashboard = useCallback(
    (type, templateId) => {
      const groupId = dashboard.group_id;
      props.createDashboard(groupId, type, templateId).then(dashboard => {
        const url = `/groups/${groupId}/dashboards/${dashboard.id}`;
        redirectTo(url);
      });
    },
    [dashboardId]
  );

  const saveDashboard = useCallback(() => {
    if (!dashboardId) return;

    // There are some changes that can be done outside of edit mode.
    // For example: toggling the showHeader option.
    if (!editMode) {
      setUnsavedChanges(false);
      props.editRights && props.saveDashboardToBackend(dashboardId);
      return;
    }

    if (props.activePage.page_type === 'normal') {
      setUnsavedChanges(false);
      setShowWidgetStore(false);
      // setEditMode(false);
      // check for editRights. This is mainly done for the autoscroll
      // functionality. The autoscroll toggle is always visible, but for those
      // who have edit rights, it will also persist to the backend.
      props.editRights && props.saveDashboardToBackend(dashboardId);
    } else {
      setShowReturnToNormalMode(true);
    }
  }, [dashboardId, props.activePage?.id]);

  const deleteDashboard = useCallback(() => {
    const groupId = dashboard.group_id;
    props.deleteDashboardFromBackend(dashboardId).then(() => {
      const url = `/groups/${groupId}/dashboard`;
      redirectTo(url);
    });
  }, [dashboardId]);

  const addWidget = useCallback(
    archetypeId => {
      setUnsavedChanges(true);

      // as default title for a widget, use the name of the archetype
      const title = props.widgetArchetypes[archetypeId].title;

      const widget = {
        position_x: 0,
        position_y: 0,
        height: 10,
        width: props.columns ? props.columns / 2 : 3,
        archetype_id: archetypeId,
        title
      };

      props.addWidget(widget, props.activePageIndex);
    },
    [props.activePageIndex, props.widgetArchetypes]
  );

  const updateTitle = useCallback(title => {
    setUnsavedChanges(true);
    props.updateCurrentDashboardFields({ title });
  }, []);

  const updateWidgetTitle = useCallback((id, title) => {
    setUnsavedChanges(true);
    props.updateWidgetFields(id, { title });
  }, []);

  const updateWidgetHeight = useCallback((id, height) => {
    props.updateWidgetFields(id, { height });
  }, []);

  const updateWidgetSettings = useCallback((id, settings) => {
    setUnsavedChanges(true);
    props.updateWidgetSettings(id, settings);
  }, []);

  const displayDashboardSettings = useCallback(() => {
    props.fetchActivityTypes();
    setShowDashboardSettings(true);
  }, []);

  const hideDashboardSettings = useCallback(() => setShowDashboardSettings(false), []);

  const fetchDashboard = useCallback(() => props.fetchCurrentDashboard(props.id), [props.id]);

  // Hooks

  // Execute on boot: get the AuthToken
  useEffect(() => {
    props.getDashboardAuthToken(props.id).then(() => {
      // IS THIS NECESSARY? This always gets the groups and subgroups, even if
      // there are no widgets on the dashboard which need this. TODO all widgets
      // need to load the data that they need, and not rely on this call
      new DashboardAPIManager().fetchGroupAndSubgroups(props.regionId);
      fetchDashboard();
      new DashboardRegistrationManager(props.dashboardDatetime.dateTime);
    });

    if (isDesktop) {
      const body = document.querySelectorAll('body')[0];
      body.style.background = dashboardBgColor;
    }
  }, []);

  useEffect(
    () => {
      if (props.groupId) props.fetchAllDashboards({ forceFetch: true, params: { group_id: props.groupId } });
      props.fetchWidgetArchetypes();
      // if we have an access_scope_group_id, register for all incident updates
      // within that scope. Only templates don't have these, and no subscription
      // needs to take place
      if (dashboard?.access_scope_group_id) {
        console.log('Registering for incident updates for Group ID', dashboard.access_scope_group_id);
        props.registerForIncidentUpdates(dashboard.access_scope_group_id);
      } else {
        console.log('No access_scope_group_id. Assuming dashboard template. ' + 'Not registering to incident updates');
      }
      props.getCurrentUser(); // for hidden availability requirement ids
    },
    // don't use props.dashboard. This will refesh dashboards after every
    // change, including to widget settings
    [dashboardId, dashboard?.access_scope_group_id]
  );

  useEffect(() => {
    const { anyActiveIncidents, dashboard, activateIncidentMode, activateNormalMode } = props;

    if (anyActiveIncidents && dashboard.incident_mode_enabled) {
      activateIncidentMode();
    } else if (!editMode) {
      activateNormalMode();
    }
  }, [props.anyActiveIncidents, dashboard]);

  useEffect(() => {
    if (!editMode) {
      if (unsavedChanges) fetchDashboard();

      setUnsavedChanges(false);
      setShowWidgetStore(false);
      setShowUnsavedChangesWarning(false);
    }
  }, [editMode]);

  useEffect(() => {
    const handleBeforeUnload = event => {
      if (unsavedChanges) event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedChanges]);

  if (props.dashboardsLoading && !dashboard) return <Loader />;
  const singleWidgetMode = isSingleWidgetMode(dashboard);
  const incidentMode = props.activePage.page_type === 'incident' && !singleWidgetMode;

  const shouldDisplayToolbar = calculateShouldDisplayToolbar({
    dashboard,
    dashboards: props.dashboards,
    editRights: props.editRights,
    stationMonitor: props.stationMonitor,
    anyActiveIncidents: props.anyActiveIncidents,
    incidentMode
  });

  return (
    <Suspense fallback={<Loader />}>
      <AccessRightsContext.Provider value={accessRights || {}}>
        <ToastContainer position="top-center" />
        <PortalDestination />
        {showUnsavedChangesWarning && (
          <UnsavedChangesWarningModal
            hideUnsavedChangesWarning={hideUnsavedChangesWarning}
            exitEditMode={exitEditMode}
            saveChanges={returnToNormalMode}
          />
        )}
        {showReturnToNormalMode && <ReturnToNormalModeModal returnToNormalMode={returnToNormalMode} />}
        <DashboardSettingsDialog
          show={showDashboardSettings}
          hide={hideDashboardSettings}
          dashboard={dashboard}
          updateDashboardFields={props.updateCurrentDashboardFields}
          saveDashboard={saveDashboard}
          activityTypes={props.activityTypes}
          groupDashboardEditRights={props.groupDashboardEditRights}
        />
        {shouldDisplayToolbar && (
          <DashboardToolbar
            incidentMode={incidentMode}
            editMode={editMode}
            activateNormalMode={props.activateNormalMode}
            activateIncidentMode={props.activateIncidentMode}
            setDashboardDatetime={props.setDashboardDatetime}
            dashboardDatetime={props.dashboardDatetime}
            unlockDashboardDatetime={props.unlockDashboardDatetime}
            dashboardTitle={dashboard.title}
            currentDashboard={dashboard}
            updateTitle={updateTitle}
            accessibleDashboards={props.dashboards}
            groupDashboardEditRights={props.groupDashboardEditRights}
            editRights={props.editRights}
            dashboardIsDefault={dashboard.default}
            showDashboardSettings={displayDashboardSettings}
            incidentModeEnabled={dashboard.incident_mode_enabled}
            enterEditMode={enterEditMode}
            exitEditMode={exitEditMode}
            showWidgetStore={setShowWidgetStore}
            showNewDashboardModal={setShowNewDashboardModal}
            saveDashboard={saveDashboard}
            deleteDashboard={deleteDashboard}
            dashboardIsPersonal={dashboard.personal}
            unsavedChanges={unsavedChanges}
            updateDashboardFields={props.updateCurrentDashboardFields}
          />
        )}
        {editMode && <div style={{ height: '2.7rem' }} />}
        {showWidgetStore && (
          <WidgetStore
            widgetArchetypes={props.widgetArchetypes}
            showWidgetStore={setShowWidgetStore}
            addWidget={addWidget}
          />
        )}
        {showNewDashboardModal && (
          <NewDashboardModal
            groupDashboardEditRights={props.groupDashboardEditRights}
            createDashboard={createDashboard}
            setShowNewDashboardModal={setShowNewDashboardModal}
            templates={props.dashboardTemplates}
          />
        )}
        <FontSizeMultiplierContext.Provider value={fontSizeMultiplier}>
          <DashboardPageResponsive
            editMode={editMode}
            activePage={props.activePage}
            saveDashboard={saveDashboard}
            updateWidgets={props.updateWidgets}
            updateWidgetSettings={updateWidgetSettings}
            activePageIndex={props.activePageIndex}
            removeWidget={props.removeWidget}
            activeIncidents={props.activeIncidents}
            setDisplayedIncidentId={props.setDisplayedIncidentId}
            widgetArchetypes={props.widgetArchetypes}
            activePageWidgetCoordinates={props.activePageWidgetCoordinates}
            dashboard={dashboard}
            singleWidgetMode={singleWidgetMode}
            displayableIncidents={props.displayableIncidents}
            displayNextIncident={props.displayNextIncident}
            currentDisplayedIncident={props.currentDisplayedIncident}
            markAsChanged={markAsChanged}
            updateWidgetTitle={updateWidgetTitle}
            updateWidgetHeight={updateWidgetHeight}
            columns={dashboard.columns}
            lockIncident={props.lockIncident}
            incidentLocked={props.incidentLocked}
            currentDisplayedIncidentId={props.currentDisplayedIncidentId}
          />
        </FontSizeMultiplierContext.Provider>
        <DashboardLoadingStatus />
        <ConnectionStatus />
      </AccessRightsContext.Provider>
    </Suspense>
  );
};

Dashboard.whyDidYouRender = true;

const mapStateToProps = state => ({
  dashboard: dashboardSelectors.selectCurrentDashboard(state),
  dashboards: dashboardSelectors.selectGroupDashboards(state),
  dashboardTemplates: dashboardSelectors.selectTemplates(state),
  dashboardsLoaded: dashboardSelectors.initialLoadComplete(state),
  dashboardDatetime: state.dashboards.dashboardDatetime,
  dashboardEditMode: state.dashboards.editMode,
  activePageIndex: state.dashboards.activePageIndex,
  activePage: dashboardSelectors.selectActivePage(state),
  activePageWidgetCoordinates: dashboardSelectors.activePageWidgetCoordinates(state),
  widgetArchetypes: widgetArchetypeSelectors.byId(state),
  widgetArchetypesLoaded: widgetArchetypeSelectors.initialLoadComplete(state),
  activityTypes: state.activityTypes.byId,
  activeIncidents: stationToIncidentsMap(state),
  anyActiveIncidents: anyActiveIncidents(state),
  displayableIncidents: selectDisplayableIncidents(state),
  currentDisplayedIncident: selectCurrentDisplayedIncident(state),
  currentDisplayedIncidentId: displayedIncidentId(state),
  incidentLocked: state.dashboards.locked
});

const mapDispatchToProps = {
  fetchCurrentDashboard: dashboardOperations.fetchCurrentDashboard,
  fetchAllDashboards: dashboardOperations.fetch,
  getDashboardAuthToken: dashboardOperations.getDashboardAuthToken,
  updateWidgets: dashboardOperations.updateWidgets,
  updateWidgetSettings: dashboardOperations.updateWidgetSettings,
  createDashboard: dashboardOperations.createDashboard,
  saveDashboardToBackend: dashboardOperations.storeInBackend,
  deleteDashboardFromBackend: dashboardOperations.deleteFromBackend,
  updateCurrentDashboardFields: dashboardOperations.updateCurrentDashboardFields,
  updateWidgetFields: dashboardOperations.updateWidget,
  removeWidget: dashboardOperations.removeWidget,
  addWidget: dashboardOperations.addWidget,
  setDashboardDatetime: dashboardOperations.setDashboardDatetime,
  unlockDashboardDatetime: dashboardOperations.unlockDashboardDatetime,
  fetchWidgetArchetypes: archetypeOperations.fetch,
  registerForIncidentUpdates: registerForIncidentUpdates,
  getCurrentUser: getCurrentUser,
  activateIncidentMode: dashboardOperations.activateIncidentMode,
  activateNormalMode: dashboardOperations.activateNormalMode,
  setDisplayedIncidentId: dashboardOperations.setDisplayedIncidentId,
  displayNextIncident: dashboardOperations.displayNextIncident,
  fetchActivityTypes: activityTypeOperations.fetch,
  setDashboardEditMode: dashboardOperations.setEditMode,
  lockIncident: dashboardOperations.lockIncident
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
